/**
 * @description Accounts controller endpoints settings
 * @module API controller / Accounts
 */

import { lowerCase } from 'lodash'
import { FORM } from 'constants/contentTypes'
import { API_ENDPOINTS } from 'constants/apiEndpoints'

module.exports = {
  editCustomization: {
    /**
     * @description Updates account modules customization
     * @param  {object} options
     * @param  {number} options.accountId
     * @returns {object} - Request settings.
     */
    settings({ accountId, values }) {
      return {
        body: values,
        endpoint: `/accounts/${accountId}`,
        method: 'PATCH',
      }
    },
  },
  editAccount: {
    /**
     * @description Updates account modules customization
     * @param  {object} options
     * @param  {number} options.accountId
     * @returns {object} - Request settings.
     */
    settings({ accountId, values }) {
      return {
        body: {
          ...values,
        },
        endpoint: `/accounts/${accountId}`,
        method: 'PUT',
      }
    },
  },
  editDepartment: {
    /**
     * @description Updates account department
     * @param  {object} options
     * @param  {number} options.accountId
     * @param  {number} options.profileId
     * @param  {string} options.department
     * @returns {object} - Request settings.
     */
    settings({ accountId, profileId, values }) {
      return {
        body: values,
        endpoint: `/account/${accountId}/profiles/${profileId}`,
        method: 'PUT',
      }
    },
  },
  getAccount: {
    /**
     * @description Get request settings to get details for an account.
     * @param {number} accountId - The account ID.
     * @returns {object} - Request settings.
     */
    settings(accountId) {
      return {
        endpoint: `/accounts/${accountId}`,
        method: 'GET',
      }
    },
  },
  getMyMembers: {
    ignoreNotFound: true,

    /**
     * @description Compute request settings to get members list for an specific account.
     * @param {object} options
     * @param {number} options.accountId - The account ID.
     * @param {number} options.pageIndex - The page index.
     * @param  {string} options.search - The filter to search.
     * @param  {string} options.workshopFilter - workshop filter.
     * @param  {string} options.sortBy - Sort by parameter.
     * @param  {boolean} options.myLicenses - My license filter.
     * @param  {number} options.expiryInDays - Expiry in days filter (replaces upcomingRenewalsDays).
     * @param  {string} options.expiryDateSort - Expiry date sort (oldest_to_newest, newest_to_oldest).
     * @param  {boolean} options.limitedMember - Limited member filter (false for pro members only).
     * @returns {object} - Request settings.
     */
    settings({
      accountId,
      pageIndex,
      search,
      workshopFilter,
      sortBy,
      myLicenses,
      expiryInDays,
      expiryDateSort,
      limitedMember,
    }) {
      const { filterBy, value: searchValue } = search || {}
      let endpoint = `/accounts/${accountId}/${API_ENDPOINTS.MY_MEMBERS}`
      const cleanSearch = searchValue ? searchValue.trim() : null
      const searchBy = searchValue ? filterBy : null
      const queryParams = []

      if (pageIndex) {
        endpoint += `/page/${pageIndex}`
      }

      if (searchBy) {
        endpoint += `/search_field/${encodeURIComponent(searchBy)}`
      }

      if (cleanSearch) {
        endpoint += `/search/${encodeURIComponent(cleanSearch)}`
      }

      if (workshopFilter && workshopFilter !== 'all') {
        queryParams.push(`workshop_filter=${lowerCase(workshopFilter)}`)
      }

      if (sortBy) {
        queryParams.push(`sort_by=${sortBy}`)
      }

      if (myLicenses !== undefined) {
        queryParams.push(`my_license=${myLicenses}`)
      }

      if (expiryInDays !== undefined) {
        queryParams.push(`expiry_in_days=${expiryInDays}`)
      }

      if (expiryDateSort) {
        queryParams.push(`expiry_date_sort=${expiryDateSort}`)
      }

      if (limitedMember !== undefined) {
        queryParams.push(`limited_member=${limitedMember}`)
      }

      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`
      }

      return {
        endpoint,
        method: 'GET',
      }
    },
  },
  getLimitedMembers: {
    ignoreNotFound: true,

    /**
     * @description Compute request settings to get limited members list for an specific account.
     * @param {object} options
     * @param {number} options.accountId - The account ID.
     * @param {number} options.pageIndex - The page index.
     * @param  {string} options.search - The filter to search.
     * @param  {string} options.workshopFilter - workshop filter.
     * @returns {object} - Request settings.
     */
    settings({ accountId, pageIndex, search, workshopFilter }) {
      const { filterBy, value: searchValue } = search || {}
      let endpoint = `/accounts/${accountId}/${API_ENDPOINTS.LIMITED_MEMBERS}`
      const cleanSearch = searchValue ? searchValue.trim() : null
      const searchBy = searchValue ? filterBy : null
      const queryParams = []

      if (pageIndex) {
        endpoint += `/page/${pageIndex}`
      }

      if (searchBy) {
        endpoint += `/search_field/${encodeURIComponent(searchBy)}`
      }

      if (cleanSearch) {
        endpoint += `/search/${encodeURIComponent(cleanSearch)}`
      }

      if (workshopFilter && workshopFilter !== 'all') {
        queryParams.push(`workshop_filter=${lowerCase(workshopFilter)}`)
      }

      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`
      }

      return {
        endpoint,
        method: 'GET',
      }
    },
  },
  updateLogo: {
    /**
     * @description Updates account logo
     * @param  {object} options
     * @param  {number} options.accountId
     * @param  {object} options.logo
     * @returns {object} - Request settings.
     */
    settings({ accountId, logo }) {
      const form = new FormData()

      if (logo) {
        form.append('logo', logo)
      }

      return {
        body: form,
        endpoint: `/accounts/${accountId}/set_logo`,
        headers: {
          enctype: FORM,
        },
        method: 'PATCH',
      }
    },
  },
  setDefaultAccount: {
    /**
     * @description Sets Default Account for the current user
     * @param  {object} options
     * @param  {number} options.accountId
     * @param  {number} options.currentAccountId
     * @returns {object} - Request settings.
     */

    settings({ accountId, currentAccountId }) {
      return {
        body: { id: accountId },
        endpoint: `/accounts/${currentAccountId}/set_default`,
        method: 'PATCH',
      }
    },
  },
  configureRenewal: {
    /**
     * @description Configure renewal settings for profiles
     * @param  {object} options
     * @param  {number} options.accountId
     * @param  {array} options.renewalEnabledProfileIds - Profile IDs to enable renewal for
     * @param  {array} options.renewalDisableProfileIds - Profile IDs to disable renewal for
     * @returns {object} - Request settings.
     */
    settings({ accountId, renewalEnabledProfileIds, renewalDisableProfileIds }) {
      return {
        body: {
          renewal_enabled_profile_ids: renewalEnabledProfileIds || [],
          renewal_disable_profile_ids: renewalDisableProfileIds || [],
        },
        endpoint: `/account/${accountId}/licenses/configure_renewal`,
        method: 'POST',
      }
    },
  },
  upgradeMembers: {
    /**
     * @description Upgrade limited members to pro members
     * @param  {object} options
     * @param  {number} options.accountId
     * @param  {array} options.memberIds - Member IDs to upgrade from limited to pro members
     * @returns {object} - Request settings.
     */
    settings({ accountId, memberIds }) {
      return {
        body: {
          profile_ids: memberIds || [],
        },
        endpoint: `/account/${accountId}/licenses/upgrade_profiles`,
        method: 'POST',
      }
    },
  },
}
